import { useState, useEffect } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import useThemeColor from "../../hooks/useThemeColor";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "./../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek.js";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";

function AddMenu() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  // Theme-aware background and text colors
  const themeBg = isDarkMode ? '#2c3034' : '#f8f9fa';
  const themeBorder = isDarkMode ? '#495057' : '#dee2e6';
  const themeText = isDarkMode ? '#e9ecef' : '#495057';
  const themeLightBg = isDarkMode ? '#495057' : '#e9ecef';

  const toggleLabelStyle = {
    color: themeText,
  };

  const [enable, setEnable] = useState(true);
  const [regularHours, setRegularHours] = useState(true);
  const [menuName, setMenuName] = useState("");
  const [originalMenuName, setOriginalMenuName] = useState("");
  const [isMenuCreated, setIsMenuCreated] = useState(false);
  const [isEditingMenuName, setIsEditingMenuName] = useState(false);
  const [epos, setEpos] = useState(true);
  const [kiosk, setKiosk] = useState(true);
  const [online, setOnline] = useState(true);
  const [delivery, setDelivery] = useState(true);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [menuGroups, setMenuGroups] = useState([
    {
      menuGroupID: 0,
      Menu_ID: 0,
      localizeID: "en",
      compID: 0,
      menuGroup_Name: "",
      menuGroup_Publish: true,
      menuGroup_Orderby: 0,
      updateDateTime: new Date().toISOString(),
    },
  ]);

  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    {
      from: "12:00 AM",
      to: "11:59 PM",
    },
  ]);

  const [schedules, setSchedules] = useState(schedule);

  const handleMenuGroupChange = (index, value) => {
    setMenuGroups((prevGroups) =>
      prevGroups.map((group, i) =>
        i === index ? { ...group, menuGroup_Name: value } : group
      )
    );
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault(); // Allow dropping
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newMenuGroups = [...menuGroups];
    const [draggedItem] = newMenuGroups.splice(draggedIndex, 1); // Remove dragged item
    newMenuGroups.splice(dropIndex, 0, draggedItem); // Insert at new position

    // Update menuGroup_Orderby for all items
    const updatedMenuGroups = newMenuGroups.map((group, idx) => ({
      ...group,
      menuGroup_Orderby: idx,
    }));

    setMenuGroups(updatedMenuGroups);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null); // Reset dragged index
  };

  const handleAddMenuGroup = () => {
    setMenuGroups([
      ...menuGroups,
      {
        menuGroupID: 0,
        Menu_ID: 0,
        localizeID: "en",
        compID: CompID,
        menuGroup_Name: "",
        menuGroup_Publish: true,
        menuGroup_Orderby: 0,
        updateDateTime: new Date().toISOString(),
      },
    ]);
  };

  const handleDeleteMenuGroup = (index) => {
    setMenuGroups((prevGroups) => prevGroups.filter((_, i) => i !== index));
  };

  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Prepare payload for the first API call
      const formattedSchedules = [];
      if (!regularHours) {
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          days.forEach((day) => {
            // If there are time slots, add them; otherwise, explicitly set an empty array
            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                // Parse time with multiple format options to handle AM/PM correctly
                const fromTime = moment(slot.from.trim(), ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm"], true);
                const toTime = moment(slot.to.trim(), ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm"], true);
                
                if (fromTime.isValid() && toTime.isValid()) {
                  formattedSchedules.push({
                    ID: 0,
                    CompID: CompID,
                    Menu_ID: 0, // Changed from menu_ID to Menu_ID to match backend
                    DayName: day,
                    OpenTime: fromTime.format("hh:mm A"),
                    CloseTime: toTime.format("hh:mm A"),
                    UpdateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        } else {
          // Process each day of the schedule separately
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              // Process checked days with time slots
              schedule.timeSlots.forEach((slot) => {
                // Parse time with multiple format options to handle AM/PM correctly
                const fromTime = moment(slot.from.trim(), ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm"], true);
                const toTime = moment(slot.to.trim(), ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "H:mm"], true);
                
                if (fromTime.isValid() && toTime.isValid()) {
                  formattedSchedules.push({
                    ID: 0,
                    CompID: CompID,
                    Menu_ID: 0, // Changed from menu_ID to Menu_ID to match backend
                    DayName: schedule.day,
                    OpenTime: fromTime.format("hh:mm A"),
                    CloseTime: toTime.format("hh:mm A"),
                    UpdateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
      }

      const menuPayload = {
        Menu_ID: 0,
        CompID: CompID,
        Menu_Name: menuName,
        Menu_Publish: enable,
        Menu_Orderby: 0,
        IsActiveforTill: epos,
        IsActiveforKIOSK: kiosk,
        IsActiveforTakeAway: online,
        IsActiveforDelivery: delivery,
        IsAvailableInTimeIntervals: !regularHours,
        UpdateDateTime: new Date().toISOString(),
        MenuWeeklyTimings: formattedSchedules.length > 0 ? formattedSchedules : null,
        products: []
      };

      // First API call to add menu
      const menuResponse = await axiosInstance.post(
        APIs.POST_ADD_MENU,
        menuPayload
      );
      if (!menuResponse.data.status) {
        showErrorToast(menuResponse.data.message || "Failed to add Menu");
        return;
      }
      
      // Get menu ID from response (handle both camelCase and PascalCase)
      const menuID = menuResponse.data.data?.Menu_ID || menuResponse.data.data?.menu_ID || menuResponse.data.data?.MenuID || menuResponse.data.data?.menuID;
      
      if (!menuID) {
        showErrorToast("Failed to get menu ID from response");
        return;
      }

      // Prepare payload for the second API call (menu groups)
      if (!menuGroups || menuGroups.length === 0) {
        showSuccessToast("Menu added successfully");
        navigate("/Food-menu/manageFoodMenu");
        return;
      }

      const updatedMenuGroups = menuGroups
        .filter((group) => group.menuGroup_Name?.length > 0)
        .map((gro) => ({
          ...gro,
          Menu_ID: menuID,
          compID: CompID,
        }));

      if (updatedMenuGroups.length > 0) {
        // Second API call to add menu groups
        const menuGroupResponse = await axiosInstance.post(
          APIs.POST_ADD_MENU_GROUP_LIST,
          updatedMenuGroups
        );
        if (!menuGroupResponse.data.status) {
          showErrorToast(
            menuGroupResponse.data.message || "Failed to add Menu Groups"
          );
          return;
        }
        showSuccessToast("Menu added successfully");
        navigate("/Food-menu/manageFoodMenu");
      } else {
        showSuccessToast("Menu added successfully");
        navigate("/Food-menu/manageFoodMenu");
      }

    } catch (error) {
      // Error adding Menu silently
      showErrorToast(
        error.response?.data?.message ||
        "An error occurred while adding the Menu"
      );
    } finally {
      setLoading(false);
    }
  };

  // Theme-aware button styles
  const themeButtonStyle = {
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    border: 'none',
    color: 'white',
    borderRadius: '12px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    boxShadow: `0 4px 12px ${themeColor}40`,
    cursor: 'pointer',
    transform: 'translateY(0)',
  };

  const themeButtonHover = (e) => {
    e.target.style.transform = 'translateY(-2px)';
    e.target.style.boxShadow = `0 6px 16px ${themeColor}60`;
  };

  const themeButtonLeave = (e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = `0 4px 12px ${themeColor}40`;
  };

  // Theme-aware card style with subtle shadow
  const themeCardStyle = {
    backgroundColor: themeBg,
    border: `1px solid ${themeBorder}`,
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: `0 4px 20px ${themeColor}20`,
  };

  // Section card style
  const sectionCardStyle = {
    backgroundColor: isDarkMode ? '#3c4043' : '#ffffff',
    border: `1px solid ${themeBorder}`,
    borderRadius: '20px',
    padding: '32px',
    marginBottom: '32px',
    boxShadow: `0 8px 32px ${themeColor}08`,
    position: 'relative',
    overflow: 'visible',
    transition: 'all 0.3s ease',
    zIndex: 1,
  };

  // Custom style for time slots section to prevent overlapping
  const timeSlotsWrapperStyle = {
    padding: '20px 16px', // Reduced padding for less space
    backgroundColor: themeBg,
    borderRadius: '12px',
    marginTop: '16px', // Reduced margin
    position: 'relative',
    zIndex: 10,
    overflow: 'visible',
  };

  // Header style for sections
  const sectionHeaderStyle = {
    color: themeText,
    fontSize: '22px',
    fontWeight: '700',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    position: 'relative',
    paddingBottom: '16px',
  };

  // Pencil edit button style
  const pencilButtonStyle = {
    background: 'none',
    border: 'none',
    color: themeText + '80',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    transition: 'all 0.2s',
    fontSize: '14px',
    marginLeft: '8px',
  };

  const handleMenuNameEdit = () => {
    setOriginalMenuName(menuName);
    setIsEditingMenuName(true);
  };

  const handleMenuNameSave = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (menuName.trim()) {
      setIsEditingMenuName(false);
    } else {
      setMenuName(originalMenuName);
      showErrorToast("Menu name cannot be empty.");
    }
  };

  const handleMenuNameCancel = () => {
    setMenuName(originalMenuName);
    setIsEditingMenuName(false);
  };

  return (
    <div className="page-wrapper" style={{
      width: '100%',
      margin: 0,
      padding: 0,
      backgroundColor: isDarkMode ? '#1a1d23' : '#f8fafc',
      minHeight: '100vh'
    }}>
      {loading && <Preloader />}

      {/* Modern Header Section */}
      <div className="content-wrapper" style={{
        background: `linear-gradient(135deg, ${themeColor}15 0%, ${themeColor}05 100%)`,
        padding: '32px 24px 24px',
        borderBottom: `1px solid ${themeBorder}`,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${themeColor}20 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }}></div>

        <div className="d-flex align-items-center justify-content-between" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ flex: 1 }}>
            <nav aria-label="breadcrumb" style={{ background: 'none', padding: 0, marginBottom: '16px' }}>
              <ol className="breadcrumb mb-0" style={{
                fontSize: '14px',
                color: themeText,
                background: 'rgba(255,255,255,0.1)',
                padding: '8px 16px',
                borderRadius: '20px',
                display: 'inline-flex',
                backdropFilter: 'blur(10px)'
              }}>
                <li className="breadcrumb-item">
                  <a href="#/" style={{ color: themeColor, textDecoration: 'none', fontWeight: '500' }}>
                    <i className="fas fa-home me-1"></i>Home
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/Food-menu/manageFoodMenu" style={{ color: themeText + 'CC', textDecoration: 'none' }}>Manage Menu</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page" style={{ color: themeColor }}>Add Menu</li>
              </ol>
            </nav>
            <h1 className="page-header mb-2" style={{
              color: themeText,
              fontSize: '36px',
              fontWeight: '800',
              marginBottom: '12px',
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              <i className="fas fa-utensils me-3" style={{ color: themeColor, fontSize: '32px' }}></i>
              Add New Menu
            </h1>
          </div>

          <button
            onClick={handleSubmit}
            className="btn px-5 d-flex align-items-center"
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: 'none',
              color: 'white',
              borderRadius: '16px',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '160px',
              boxShadow: `0 8px 25px ${themeColor}40`,
              transform: 'translateY(0)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = `0 12px 35px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 8px 25px ${themeColor}40`;
            }}
          >
            <i className="fas fa-check me-2"></i>
            Save Menu
          </button>
        </div>
      </div>

      <div className="container-fluid" style={{ padding: '32px 24px' }}>
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-lg" style={{
              background: isDarkMode ? '#2d3436' : '#ffffff',
              borderRadius: '24px',
              overflow: 'visible',
              boxShadow: `0 20px 60px ${themeColor}15`,
              border: `1px solid ${themeBorder}`,
            }}>
              {/* Card Body */}
              <div style={{ padding: '40px' }}>
                {!isMenuCreated ? (
                  <div className="row">
                    <div className="col-12">
                      {/* Menu Name and Settings Row */}
                      <div className="row g-4">
                        {/* Menu Name - 6 columns */}
                        <div className="col-6">
                          <div style={sectionCardStyle}>
                            <div style={sectionHeaderStyle}>
                              <div style={{
                                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                                padding: '12px',
                                borderRadius: '16px',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '48px',
                                height: '48px',
                                boxShadow: `0 4px 16px ${themeColor}40`
                              }}>
                                <i className="fas fa-tag" style={{ fontSize: '18px' }}></i>
                              </div>
                              <div>
                                <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Menu Name</div>
                              </div>
                            </div>
                            <FormInput
                              label={""}
                              placeHolder={"Enter a menu Name"}
                              type={"text"}
                              value={menuName}
                              onchange={(e) => setMenuName(e.target.value)}
                              style={{
                                borderRadius: '10px',
                                border: `1px solid ${themeBorder}`,
                                backgroundColor: isDarkMode ? '#495057' : '#fff',
                                color: themeText,
                                padding: '14px 18px',
                                fontSize: '16px',
                                boxShadow: `0 2px 4px ${themeColor}10`,
                              }}
                            />
                          </div>
                        </div>

                        {/* Combined Settings - 6 columns */}
                        <div className="col-6">
                          <div style={sectionCardStyle}>
                            <div style={sectionHeaderStyle}>
                              <div style={{
                                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                                padding: '12px',
                                borderRadius: '16px',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '48px',
                                height: '48px',
                                boxShadow: `0 4px 16px ${themeColor}40`
                              }}>
                                <i className="fas fa-cog" style={{ fontSize: '18px' }}></i>
                              </div>
                              <div>
                                <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Menu Settings</div>
                                <div style={{ fontSize: '14px', color: themeText + '80', margin: '4px 0 0', fontWeight: '400' }}>Configure availability and channels</div>
                              </div>
                            </div>
                            <div className="row g-3">
                              {/* Row 1: Status & Online Orders */}
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={enable}
                                  onToggle={() => setEnable(!enable)}
                                  label={"Status"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>

                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={online}
                                  onToggle={() => setOnline(!online)}
                                  label={"Online Orders"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>

                              {/* Row 2: Delivery & E-POS */}
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={delivery}
                                  onToggle={() => setDelivery(!delivery)}
                                  label={"Delivery"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={epos}
                                  onToggle={() => setEpos(!epos)}
                                  label={"E-POS"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>

                              <div className="col-6" style={{ display: 'none' }}>
                                <ToggleSwitch
                                  isChecked={kiosk}
                                  onToggle={() => setKiosk(!kiosk)}
                                  label={"KIOSK"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>

                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="text-center" style={{ marginTop: '40px' }}>
                        <button
                          className="btn"
                          style={{
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                            border: 'none',
                            color: 'white',
                            borderRadius: '20px',
                            padding: '20px 48px',
                            fontSize: '18px',
                            fontWeight: '700',
                            minWidth: '280px',
                            height: '64px',
                            boxShadow: `0 12px 40px ${themeColor}50`,
                            transform: 'translateY(0)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px'
                          }}
                          onClick={() => {
                            if (menuName.trim()) {
                              setIsMenuCreated(true);
                            } else {
                              showErrorToast("Please enter a menu name.");
                            }
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-4px)';
                            e.target.style.boxShadow = `0 16px 50px ${themeColor}70`;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = `0 12px 40px ${themeColor}50`;
                          }}
                        >
                          <div style={{
                            background: 'rgba(255,255,255,0.2)',
                            padding: '8px',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)'
                          }}>
                            <i className="fas fa-magic" style={{ fontSize: '16px' }}></i>
                          </div>
                          Create Menu & Add Groups
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12">
                      {/* Menu Name and Settings Row - Same as first view */}
                      <div className="row g-4">
                        {/* Menu Name - 6 columns */}
                        <div className="col-6">
                          <div style={sectionCardStyle}>
                            <div style={sectionHeaderStyle}>
                              <div style={{
                                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                                padding: '12px',
                                borderRadius: '16px',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '48px',
                                height: '48px',
                                boxShadow: `0 4px 16px ${themeColor}40`
                              }}>
                                <i className="fas fa-tag" style={{ fontSize: '18px' }}></i>
                              </div>
                              <div>
                                <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Menu Name</div>
                                <div style={{ fontSize: '14px', color: themeText + '80', margin: '4px 0 0', fontWeight: '400' }}>Give your menu a memorable name</div>
                              </div>
                            </div>
                            {isEditingMenuName ? (
                              <div className="d-flex flex-column gap-3">
                                <FormInput
                                  type="text"
                                  value={menuName}
                                  onchange={(e) => setMenuName(e.target.value)}
                                  onBlur={handleMenuNameSave}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleMenuNameSave();
                                    }
                                  }}
                                  autoFocus
                                  style={{
                                    borderRadius: '10px',
                                    border: `1px solid ${themeColor}`,
                                    backgroundColor: isDarkMode ? '#495057' : '#fff',
                                    color: themeText,
                                    padding: '14px 18px',
                                    fontSize: '16px',
                                    boxShadow: `0 2px 4px ${themeColor}10`,
                                  }}
                                />
                                <div className="d-flex gap-2">
                                  <button
                                    onClick={handleMenuNameSave}
                                    className="btn"
                                    style={{
                                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                                      border: 'none',
                                      color: 'white',
                                      padding: '10px 20px',
                                      fontSize: '14px',
                                      borderRadius: '8px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={handleMenuNameCancel}
                                    className="btn"
                                    style={{
                                      background: 'transparent',
                                      border: `1px solid ${themeText + '40'}`,
                                      color: themeText,
                                      padding: '10px 20px',
                                      fontSize: '14px',
                                      borderRadius: '8px',
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px 20px',
                                background: isDarkMode ? '#495057' : '#f8f9fa',
                                borderRadius: '12px',
                                border: `1px solid ${themeBorder}`
                              }}>
                                <div style={{
                                  fontSize: '18px',
                                  fontWeight: '600',
                                  color: themeText,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px'
                                }}>
                                  <i className="fas fa-check-circle" style={{ color: themeColor, fontSize: '20px' }}></i>
                                  {menuName}
                                </div>
                                <button
                                  onClick={handleMenuNameEdit}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: themeText + '80',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.color = themeColor;
                                    e.target.style.backgroundColor = themeColor + '20';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.color = themeText + '80';
                                    e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <i className="fas fa-pencil-alt"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Menu Settings - 6 columns */}
                        <div className="col-6">
                          <div style={sectionCardStyle}>
                            <div style={sectionHeaderStyle}>
                              <div style={{
                                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                                padding: '12px',
                                borderRadius: '16px',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '48px',
                                height: '48px',
                                boxShadow: `0 4px 16px ${themeColor}40`
                              }}>
                                <i className="fas fa-cog" style={{ fontSize: '18px' }}></i>
                              </div>
                              <div>
                                <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Menu Settings</div>
                                <div style={{ fontSize: '14px', color: themeText + '80', margin: '4px 0 0', fontWeight: '400' }}>Configure availability and channels</div>
                              </div>
                            </div>
                            <div className="row g-3">
                              {/* Row 1: Enable Menu & Online Orders */}
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={enable}
                                  onToggle={() => setEnable(!enable)}
                                  label={"Enable this menu"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={online}
                                  onToggle={() => setOnline(!online)}
                                  label={"Online Orders"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>

                              {/* Row 2: Delivery & E-POS */}
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={delivery}
                                  onToggle={() => setDelivery(!delivery)}
                                  label={"Delivery"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>
                              <div className="col-6">
                                <ToggleSwitch
                                  isChecked={epos}
                                  onToggle={() => setEpos(!epos)}
                                  label={"E-POS"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>

                              <div className="col-6" style={{ display: 'none' }}>
                                <ToggleSwitch
                                  isChecked={kiosk}
                                  onToggle={() => setKiosk(!kiosk)}
                                  label={"KIOSK"}
                                  disabled={false}
                                  labelStyle={toggleLabelStyle}
                                  themeColor={themeColor}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Time Slot Selection Section */}
                    <div style={sectionCardStyle}>
                      <div style={sectionHeaderStyle}>
                        <div style={{
                          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                          padding: '12px',
                          borderRadius: '16px',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '48px',
                          height: '48px',
                          boxShadow: `0 4px 16px ${themeColor}40`
                        }}>
                          <i className="fas fa-clock" style={{ fontSize: '18px' }}></i>
                        </div>
                        <div>
                          <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Time Slot Configuration</div>
                          <div style={{ fontSize: '14px', color: themeText + '80', margin: '4px 0 0', fontWeight: '400' }}>Set custom availability hours</div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <ToggleSwitch
                          isChecked={regularHours}
                          onToggle={() => setRegularHours(!regularHours)}
                          label={"Available during regular business hours"}
                          disabled={false}
                          labelStyle={toggleLabelStyle}
                          themeColor={themeColor}
                        />
                      </div>
                      {!regularHours && (
                        <div style={timeSlotsWrapperStyle}>
                          <ToggleSwitch
                            isChecked={useSameTimeSlots}
                            onToggle={() => {
                              setUseSameTimeSlots(!useSameTimeSlots);
                              if (!useSameTimeSlots) {
                                applyCommonTimeSlotToAll();
                              }
                            }}
                            label={"Apply same time slots to all days"}
                            disabled={false}
                            labelStyle={toggleLabelStyle}
                            themeColor={themeColor}
                          />
                          {useSameTimeSlots ? (
                            <CommonTimeSlotForAllWeek
                              setCommonTimeSlots={setCommonTimeSlot}
                              commonTimeSlots={commonTimeSlot}
                              isProfilePage={false}
                            />
                          ) : (
                            <ScheduleManager
                              schedules={schedules}
                              setSchedules={setSchedules}
                              isProfilePage={false}
                              isDarkMode={isDarkMode}
                              themeColor={themeColor}
                            />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Menu Groups Section */}
                    <div style={sectionCardStyle}>
                      <div style={sectionHeaderStyle}>
                        <div style={{
                          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}CC 100%)`,
                          padding: '12px',
                          borderRadius: '16px',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '48px',
                          height: '48px',
                          boxShadow: `0 4px 16px ${themeColor}40`
                        }}>
                          <i className="fas fa-list-ul" style={{ fontSize: '18px' }}></i>
                        </div>
                        <div>
                          <div style={{ fontSize: '22px', fontWeight: '700', color: themeText, margin: 0 }}>Menu Groups</div>
                          <div style={{ fontSize: '14px', color: themeText + '80', margin: '4px 0 0', fontWeight: '400' }}>Organize your menu items into categories. Drag to reorder.</div>
                        </div>
                      </div>
                      {menuGroups.map((group, index) => (
                        <div
                          key={index}
                          className="row mb-3 align-items-center"
                          style={{
                            backgroundColor: isDarkMode ? '#495057' : '#f8f9fa',
                            borderRadius: '12px',
                            padding: '20px',
                            border: `1px solid ${themeBorder}`,
                            cursor: 'grab',
                            transition: 'all 0.3s ease',
                            opacity: draggedIndex === index ? 0.8 : 1,
                            boxShadow: `0 2px 8px ${themeColor}10`,
                            transform: draggedIndex === index ? 'scale(0.98)' : 'scale(1)',
                          }}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                        >
                          <div className="col-1 d-flex align-items-center justify-content-center">
                            <i
                              className="fas fa-grip-vertical text-muted"
                              style={{ fontSize: '20px', cursor: 'grab', color: themeText + '60' }}
                            />
                          </div>
                          <div className="col-10">
                            <FormInput
                              placeHolder={`Group ${index + 1} (e.g., Appetizers, Main Course)`}
                              type={"text"}
                              value={group.menuGroup_Name}
                              onchange={(e) => handleMenuGroupChange(index, e.target.value)}
                              style={{
                                borderRadius: '10px',
                                border: `1px solid ${themeBorder}`,
                                backgroundColor: isDarkMode ? '#495057' : '#fff',
                                color: themeText,
                                padding: '12px 16px',
                                fontSize: '16px',
                              }}
                            />
                          </div>
                          {index > 0 && (
                            <div className="col-1 d-flex align-items-center justify-content-end">
                              <i
                                className="fas fa-trash text-danger"
                                style={{
                                  fontSize: '18px',
                                  cursor: 'pointer',
                                  padding: '8px',
                                  borderRadius: '50%',
                                  transition: 'all 0.2s',
                                }}
                                onClick={() => handleDeleteMenuGroup(index)}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = '#dc354520';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="text-center mt-4">
                        <button
                          className="btn"
                          onClick={handleAddMenuGroup}
                          style={{
                            background: 'transparent',
                            border: `2px dashed ${themeColor}`,
                            color: themeColor,
                            padding: '12px 24px',
                            fontSize: '15px',
                            borderRadius: '12px',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = `${themeColor}15`;
                            e.target.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fas fa-plus me-2"></i>
                          Add Group
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
}

export default AddMenu;